import { ActionTree, ActionContext } from 'vuex';
import { get, map } from 'lodash';
import { RootState } from '@/store';
import { State } from './state';
import { Mutations } from './mutations';
import { BookViewMutationTypes } from './mutation-types';
import { BookViewActionTypes } from './action-types';
import {
  BookApiOptions,
  ClientApiResponse,
  HeatmapSliderOptions,
  ClientHeatmapApiResponse,
} from './types';

import ServerAPI, {
  combineQueryParams,
  handleError,
} from '@/services/serverAPI';
import { FeatureFlag, config, getFeatureFlag } from '@/services/config';
import { State as GeneralState } from '@/store/modules/general';
import { getters as generalGetters } from '@/store/modules/general/getters';

type AugmentedAction = {
  commit<K extends keyof Mutations>(
    key: K,
    payload: Parameters<Mutations[K]>[1]
  ): ReturnType<Mutations[K]>;
} & Omit<ActionContext<State, RootState>, 'commit'>;

export interface Actions {
  [BookViewActionTypes.SEARCH_BOOK](
    { commit }: AugmentedAction,
    payload: {
      options: BookApiOptions;
      generalState: GeneralState;
    }
  ): Promise<void>;
  [BookViewActionTypes.SEARCH_BOOK_HEATMAP](
    { commit }: AugmentedAction,
    payload: {
      options: BookApiOptions;
      generalState: GeneralState;
    }
  ): Promise<void>;
  [BookViewActionTypes.CLEAR_BOOK_STATE](
    { commit }: AugmentedAction,
    payload: undefined
  ): Promise<void>;
  [BookViewActionTypes.SET_SLIDER_OPTIONS](
    { commit }: AugmentedAction,
    payload: HeatmapSliderOptions
  ): Promise<void>;
  [BookViewActionTypes.SET_BOOK_SETTINGS](
    { commit }: AugmentedAction,
    payload: BookApiOptions
  ): Promise<void>;
  [BookViewActionTypes.RESET_BOOK_SETTINGS](
    { commit }: AugmentedAction,
    payload: undefined
  ): Promise<void>;
}

export const actions: ActionTree<State, RootState> & Actions = {
  async [BookViewActionTypes.SEARCH_BOOK]({ commit }, payload) {
    commit(BookViewMutationTypes.BOOK_STORE_SET_LOADING, true);
    try {
      const paramString = combineQueryParams({
        size: payload.options.pageSize,
        start: payload.options.pageStart,
        filters: payload.options.filters,
        ['sort_descending']: payload.options.sortDesc,
        ['sort_by']: payload.options.sortKey,
        ['aggregate_by']: payload.options.aggregateBy,
        ['exact_id_search']: payload.options.exactIdSearch,
      });

      commit(BookViewMutationTypes.BOOK_STORE_SET_SETTINGS, payload.options);

      let searchTerm = '';
      let filters = '';

      if (payload.options.searchTerm) {
        const matchingClientIds = generalGetters.getClientIdsByNameSearch(
          payload.generalState,
          payload.options.searchTerm
        );

        searchTerm = matchingClientIds.join(',');
        filters += `&search_field=${payload.options.searchType}&search_string=${searchTerm}`;
      }

      const linkClientsEnabled = getFeatureFlag(FeatureFlag.LINK_CLIENTS)
        .enabled;

      const clientsEndpoint = get(
        config.layout,
        'bookView.clientDataEndpoint',
        'clients'
      );

      const groupsEndpoint = get(
        config.layout,
        'bookView.groupsDataEndpoint',
        'groups'
      );

      const endpoint = linkClientsEnabled ? groupsEndpoint : clientsEndpoint;

      const clientData: ClientApiResponse = await ServerAPI.get(
        `/${endpoint}${paramString}${filters}`
      );
      commit(BookViewMutationTypes.BOOK_STORE_SET_DATA, {
        clientData: clientData.results,
        totalResultCount: clientData.count,
      });
    } catch (e) {
      handleError(e);
      commit(BookViewMutationTypes.BOOK_STORE_SET_DATA, {
        clientData: [],
        totalResultCount: 0,
      });
    } finally {
      commit(BookViewMutationTypes.BOOK_STORE_SET_LOADING, false);
    }
  },
  async [BookViewActionTypes.SEARCH_BOOK_HEATMAP]({ commit }, payload) {
    commit(BookViewMutationTypes.BOOK_STORE_SET_HEATMAP_LOADING, true);

    try {
      const paramString = combineQueryParams({
        size: 999999,
        filters: payload.options.filters,
        ['sort_descending']: payload.options.sortDesc,
        ['sort_by']: payload.options.sortKey,
        ['aggregate_by']: payload.options.aggregateBy,
        ['exact_id_search']: payload.options.exactIdSearch,
      });

      let searchTerm = '';
      let filters = '';

      if (payload.options.searchTerm) {
        const matchingClientIds = generalGetters.getClientIdsByNameSearch(
          payload.generalState,
          payload.options.searchTerm
        );

        searchTerm = matchingClientIds.join(',');
        filters += `&search_field=${payload.options.searchType}&search_string=${searchTerm}`;
      }

      const linkClientsEnabled = getFeatureFlag(FeatureFlag.LINK_CLIENTS)
        .enabled;

      const clientsEndpoint = get(
        config.layout,
        'bookView.heatmapDataEndpoint',
        'clients'
      );

      const groupsEndpoint = get(
        config.layout,
        'bookView.groupsHeatmapDataEndpoint',
        'groups'
      );

      const endpoint = linkClientsEnabled ? groupsEndpoint : clientsEndpoint;

      const heatmapResponse: ClientHeatmapApiResponse = await ServerAPI.get(
        `/${endpoint}${paramString}${filters}`
      );

      commit(BookViewMutationTypes.BOOK_STORE_SET_HEATMAP_DATA, {
        data: map(heatmapResponse.results, item => {
          let data = {};
          const nestedValueKeys = ['clientSpecificValues', 'aggregate'];

          for (const key in item) {
            if (!nestedValueKeys.includes(key)) {
              data[key] = item[key];
            }
          }

          data = {
            ...data,
            ...item.clientSpecificValues,
            ...item.aggregate,
          };

          return data;
        }),
      });
    } catch (e) {
      handleError(e);
      commit(BookViewMutationTypes.BOOK_STORE_SET_HEATMAP_DATA, {
        data: [],
      });
    } finally {
      commit(BookViewMutationTypes.BOOK_STORE_SET_HEATMAP_LOADING, false);
    }
  },
  async [BookViewActionTypes.CLEAR_BOOK_STATE]({ commit }, payload) {
    commit(BookViewMutationTypes.BOOK_STORE_CLEAR_DATA, payload);
  },
  async [BookViewActionTypes.SET_SLIDER_OPTIONS]({ commit }, payload) {
    commit(BookViewMutationTypes.BOOK_STORE_SET_SLIDER_OPTIONS, payload);
  },
  async [BookViewActionTypes.SET_BOOK_SETTINGS]({ commit }, payload) {
    commit(BookViewMutationTypes.BOOK_STORE_SET_SETTINGS, payload);
  },
  async [BookViewActionTypes.RESET_BOOK_SETTINGS]({ commit }, payload) {
    commit(BookViewMutationTypes.BOOK_STORE_RESET_DATA, payload);
  },
};
